// core/services/firebase/counterService.ts
import { collection, doc, setDoc, increment, updateDoc } from 'firebase/firestore';
import { db } from './config';

/**
 * Service to handle distributed counters leveraging the firestore-counter extension.
 * This avoids the 1-update-per-second limit on single documents.
 */
export const counterService = {
  /**
   * Increments a distributed counter.
   * Instead of updating the main document, it writes to a random shard in the subcollection.
   * The firestore-counter extension will aggregate these shards into the main document.
   * 
   * @param parentDocPath Path to the parent document (e.g., 'posts/POST_ID')
   * @param fieldName The name of the field to increment
   * @param value The value to increment by (default 1)
   * @param numShards Number of shards to use (more shards = higher concurrency, default 10)
   */
  async incrementCounter(
    parentDocPath: string, 
    fieldName: string, 
    value: number = 1, 
    numShards: number = 10
  ): Promise<void> {
    // Generate a random shard ID
    const shardId = Math.floor(Math.random() * numShards).toString();
    
    // Path to the shard: parentDoc/INTERNAL_COLLECTION/shardId
    // According to distributed-counters.env: INTERNAL_COLLECTION=_counters_shards
    const shardRef = doc(db, `${parentDocPath}/_counters_shards`, shardId);
    
    try {
      // Update the shard document. 
      // The extension usually expects a 'count' field or the fieldName itself.
      // Based on standard firestore-counter extension behavior:
      await setDoc(shardRef, {
        [fieldName]: increment(value)
      }, { merge: true });
      
      console.log(`[Counter] Shard ${shardId} incremented for ${parentDocPath} -> ${fieldName}`);
    } catch (error) {
      console.error(`[Counter] Error incrementing shard for ${parentDocPath}:`, error);
      // Fallback: direct update if shards fail (optional, but keep it robust)
      const parentRef = doc(db, parentDocPath);
      await updateDoc(parentRef, {
        [fieldName]: increment(value)
      });
    }
  }
};
